<template>
  <div>
    <div class="modal-body">
      <div
        class="modal-body--alert alert alert-danger d-none"
        role="alert"
      />
      <div class="row h-100 w-100">
        <div
          class="modal-body--preview col-6"
        >
          <PSDArtboardsPreview
            v-if="artboards.length > 1"
            :image-src="entireImageThumbnail | share"
            :artboards="artboards"
            :source-size="{ width: parsedPsdModel.width, height: parsedPsdModel.height }"
            @artboard-click="handlePreviewImageArtboardClick"
          />
          <div v-else>
            <img
              v-if="!previewTemplateThumbnail"
              class="modal-body--preview__img"
              :class="{'d-none': isImageLoading}"
              :src="entireImageThumbnail | share"
              alt="preview"
              @load="imageLoadedHandler"
            >
            <img
              v-else
              class="modal-body--preview__img"
              :src="previewTemplateThumbnail"
              alt="preview"
            >
            <div
              class="spinner-border modal-body--preview__spinner"
              :class="{'d-none': !isImageLoading}"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>
            <div
              v-if="previewTemplateThumbnail"
              class="modal-body--preview__img-close"
              @click="setPreviewTemplateThumbnail({ previewTemplateThumbnail: '' })"
            >
              <svg
                width="20px"
                height="20px"
              >
                <use href="/img/icons/sprite.svg#file-circle-close" />
              </svg>
            </div>
          </div>
        </div>
        <LayersTree
          class="modal-body--layers-wrapper col-6"
          :templates="templates"
        />
      </div>
    </div>
    <div class="row">
      <div
        class="col d-flex justify-content-end align-items-start"
        style="gap: 0.25rem"
      >
        <b-button
          class="mr-1"
          :disabled="isSavingNodesChanges"
          @click="cancel"
        >
          Close
        </b-button>
        <b-button
          v-if="isNodesChangesShouldBeSaved"
          class="d-flex align-items-center"
          :disabled="isSavingNodesChanges"
          @click="saveNodesChanges"
        >
          <span>
            {{ isSavingNodesChanges ? 'Saving changes...' : 'Save changes' }}
            <i
              v-if="isSavingNodesChanges"
              class="spinner-border spinner-border-sm m-0"
            />
          </span>
        </b-button>
        <div>
          <button
            v-if="isTemplateFeatures && isImportIntoTemplate"
            type="button"
            class="btn btn-primary"
            :disabled="isUpdatingPreviewTemplate"
            @click="updatePreviewTemplate"
          >
            <span
              class="spinner-border spinner-border-sm"
              :class="{'d-none': !isUpdatingPreviewTemplate}"
              role="status"
              aria-hidden="true"
            />
            Preview
          </button>
          <button
            v-if="isTemplateFeatures"
            type="button"
            class="btn btn-primary"
            :disabled="!isAddToTemplateButtonAvailable"
            @click="addToTemplate"
          >
            <span
              class="spinner-border spinner-border-sm"
              :class="{'d-none': isAddToTemplateButtonAvailable}"
              role="status"
              aria-hidden="true"
            />
            Add to template
          </button>
          <button
            v-if="availableDefaultFlow"
            type="button"
            class="btn btn-primary"
            :disabled="isExtractingAssets || isSavingNodesChanges"
            @click="proceedByFlow"
          >
            <span
              class="spinner-border spinner-border-sm"
              :class="{'d-none': !isExtractingAssets}"
              role="status"
              aria-hidden="true"
            />
            {{ proceedButtonLabel }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="isTemplateFeatures"
      class="position-relative"
    >
      <div class="template-features-buttons">
        <b-button
          variant="secondary"
          @click="iccProfileOpen"
        >
          Adjust ICC
        </b-button>
        <b-button
          v-if="isProductRecognitionAvailable"
          variant="primary"
          :disabled="isProductRecognitionProcessing"
          @click="requestProductRecognition"
        >
          Recognize product images
        </b-button>
      </div>
    </div>

    <!-- Import into template options (shown when importing into existing template) -->
    <div
      v-if="isTemplateFeatures && isImportIntoTemplate"
      class="row mt-2 mb-2"
    >
      <div class="col-6">
        <b-form-checkbox
          id="autoMapToFields"
          :checked="isAutoMapToFields"
          @change="setAutoMapFields()"
        >
          Auto map to fields
        </b-form-checkbox>

        <b-form-checkbox
          v-b-tooltip.hover.left
          title="Checking this option will remove any default text from the destination templates"
          :checked="isRemovePlaceholderText"
          @change="setRemovePlaceholderText({ isRemovePlaceholderText: !isRemovePlaceholderText })"
        >
          Remove placeholder text
        </b-form-checkbox>
      </div>
      <div class="col-6">
        <b-form-checkbox
          :checked="isUseSourceTextSettings"
          @change="setUseSourceTextSettings({ isUseSourceTextSettings: !isUseSourceTextSettings })"
        >
          Use source text settings
        </b-form-checkbox>
      </div>
    </div>

    <!-- Two-column checkbox layout -->
    <div class="row mt-2 psd-import-options">
      <div class="col-6">
        <b-form-checkbox
          v-if="isOpenBannerInBuilderAvailable"
          id="isOpenInBannerBuilder"
          :checked="isOpenInBannerBuilder"
          @change="setIsOpenInBannerBuilder(!isOpenInBannerBuilder)"
        >
          Open in Banner Builder
        </b-form-checkbox>

        <b-form-checkbox
          v-if="isUseProductImagesFromPSDAvailable"
          id="isUseProductImagesFromPSD"
          :checked="isUseProductImagesFromPSD"
          @change="setIsUseProductImagesFromPSD({ value: !isUseProductImagesFromPSD })"
        >
          Use product images from PSD
        </b-form-checkbox>

        <b-form-checkbox
          v-if="isReplaceExistingProductAvailable"
          id="replaceExistingProduct"
          :checked="isReplaceExistingProduct"
          @change="setReplaceExistingProduct({ isReplaceExistingProduct: !isReplaceExistingProduct })"
        >
          Replace existing product images
        </b-form-checkbox>

        <b-form-checkbox
          v-if="isSavePsdAvailable"
          id="savePsd"
          :checked="isSavePsd"
          @change="setSavePsd({ value: !isSavePsd })"
        >
          Save PSD
        </b-form-checkbox>

        <b-form-checkbox
          v-if="isImportWithoutLayersAvailable"
          id="addWithoutLayers"
          :checked="isImportWithoutLayers"
          @change="setIsImportWithoutLayers( { isImportWithoutLayers: !isImportWithoutLayers })"
        >
          Add without layers
        </b-form-checkbox>
      </div>

      <div class="col-6">
        <b-form-checkbox
          v-if="isTreatMultipleSpacesAsSingleAvailable"
          id="isTreatMultipleSpacesLineBreak"
          :checked="isTreatMultipleSpacesAsSingle"
          @change="setTreatMultipleSpacesAsSingle({ isTreatMultipleSpacesAsSingle: !isTreatMultipleSpacesAsSingle })"
        >
          Treat multiple spaces as single space
        </b-form-checkbox>

        <b-form-checkbox
          v-if="isPhotoshopTextGeometryAvailable"
          id="textGeometryMode"
          v-b-tooltip.hover.left
          title="Preserves Photoshop paragraph box dimensions during this import."
          :checked="textGeometryMode === 'photoshop'"
          @change="setTextGeometryMode({ textGeometryMode: textGeometryMode === 'photoshop' ? 'legacy' : 'photoshop' })"
        >
          Preserve Photoshop text geometry (Beta)
        </b-form-checkbox>

        <small
          v-else-if="isTemplateFeatures"
          class="d-block text-muted"
        >
          Reparse PSD to enable beta text geometry.
        </small>

        <b-form-checkbox
          v-if="isGroupGroupedLayersAvailable"
          id="isGroupGroupedLayers"
          :checked="isGroupGroupedLayers"
          @change="setIsGroupGroupedLayers({ isGroupGroupedLayers: !isGroupGroupedLayers })"
        >
          Group grouped layers
        </b-form-checkbox>

        <b-form-checkbox
          v-if="isResizeAllowedAvailable"
          id="isResizeAllowed"
          :checked="isResizeAllowed"
          @change="setIsResizeAllowed({ isResizeAllowed: !isResizeAllowed })"
        >
          Resize if larger than 5000x5000 px
        </b-form-checkbox>

        <b-form-checkbox
          v-if="showSaveImagesToTheme"
          id="createIndividualAssets"
          :checked="isCreateIndividualAssets"
          @change="setCreateIndividualAssets({ isCreateIndividualAssets: !isCreateIndividualAssets })"
        >
          Create individual assets
        </b-form-checkbox>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import {MUTATIONS} from "../../../store/psd-layers";
import {ApplyStrategy} from "@frontend/services/api/psd-layers";
import LayersTree from "@frontend/components/modules/psd-layers/LayersTree.vue";
import {EVENTS, IccProfileEventBroker} from "@frontend/components/modules/psd-layers/services/icc-profile-event-broker";
import share from "@frontend/components/modules/psd-layers/mixins/share";
import PSDArtboardsPreview from "@frontend/components/modules/psd-layers/PSDArtboardsPreview.vue";

const { mapGetters, mapActions, mapMutations } = createNamespacedHelpers('psdLayers')

const DEFAULT_FLOW = [
  ApplyStrategy.FLOWS.SAVE_TO_FILE_BROWSER,
  ApplyStrategy.FLOWS.CREATE_TEMPLATE_FROM_ADMIN
]

const SAVE_PSD_NOT_AVAILABLE = [
  ApplyStrategy.FLOWS.SAVE_TO_FILE_BROWSER,
  ApplyStrategy.FLOWS.CREATE_TEMPLATE_FROM_ADMIN
]

export default {
  name: "LayerSelector",
  components: { PSDArtboardsPreview, LayersTree },
  mixins: [
    share
  ],
  props: {
    showSaveImagesToTheme: {
      type: Boolean,
      default: () => false
    },
  },
  data: () => ({
    isImageLoading: true,
  }),
  computed: {
    ...mapGetters([
      'entireImageThumbnail',
      'previewTemplateThumbnail',
      'templates',
      'selectedTemplates',
      'flow',
      'isGenerating',
      'isExtractingAssets',
      'isSavePsd',
      'isCreateIndividualAssets',
      'isUpdatingPreviewTemplate',
      'isTreatMultipleSpacesAsSingle',
      'selectedTemplates',
      'isImportIntoTemplate',
      'isReplaceExistingProduct',
      'isGroupGroupedLayers',
      'isResizeAllowed',
      'isAutoMapToFields',
      'isRemovePlaceholderText',
      'isTemplateFeatures',
      'isUseSourceTextSettings',
      'isSavingNodesChanges',
      'nodesChanges',
      'isProductRecognitionProcessing',
      'isUseProductImagesFromPSD',
      'files',
      'isOpenInBannerBuilder',
      'isImportWithoutLayers',
      'parsedPsdModel',
      'currentPreviewIndex',
      'textGeometryMode'
    ]),
    availableDefaultFlow() {
      return DEFAULT_FLOW.includes(this.flow)
    },
    proceedButtonLabel() {
      return this.flow === ApplyStrategy.FLOWS.CREATE_TEMPLATE_FROM_ADMIN
        ? 'Import Template'
        : 'Extract assets';
    },
    isSavePsdAvailable() {
      return !SAVE_PSD_NOT_AVAILABLE.includes(this.flow)
    },
    isNodesChangesShouldBeSaved() {
      return this.flow === ApplyStrategy.FLOWS.SAVE_TO_FILE_BROWSER && this.nodesChanges.length > 0;
    },
    isUseProductImagesFromPSDAvailable() {
      return this.isTemplateFeatures;
    },
    isImportWithoutLayersAvailable() {
      return this.isTemplateFeatures && !this.isImportIntoTemplate;
    },
    isAddToTemplateButtonAvailable() {
      const isSomeFileNotReady = this.files.some(file => (file.isLoading || file.isQueued || file.isError));

      return !isSomeFileNotReady && !this.isGenerating;
    },
    isProductRecognitionAvailable() {
      // return this.isTemplateFeatures && this.isImportIntoTemplate;
      //disable button because API is deprecated
      return false;
    },
    isGroupGroupedLayersAvailable() {
      return this.isTemplateFeatures;
    },
    isResizeAllowedAvailable() {
      return this.isTemplateFeatures;
    },
    isOpenBannerInBuilderAvailable() {
      return this.isTemplateFeatures;
    },
    isReplaceExistingProductAvailable() {
      return this.isTemplateFeatures;
    },
    isTreatMultipleSpacesAsSingleAvailable() {
      return this.isTemplateFeatures;
    },
    isPhotoshopTextGeometryAvailable() {
      const hasGeometry = (nodes = []) => nodes.some(node => {
        if (node?.data?.text?.psdGeometry?.boxBounds) {
          return true;
        }

        return hasGeometry(node?.children || []);
      });

      return this.isTemplateFeatures && hasGeometry(this.templates);
    },
    artboards() {
      return this.parsedPsdModel.templates.filter(template => !template.extracted);
    }
  },
  watch: {
    entireImageThumbnail() {
      this.isImageLoading = true;
    }
  },
  methods: {
    ...mapActions([
      'cancel',
      'addToTemplate',
      'updatePreviewTemplate',
      'proceedByFlow',
      'requestProductRecognition',
      'saveNodesChanges',
      'setAutoMapFields',
    ]),
    ...mapMutations({
      setSelectedTemplate: MUTATIONS.SET_SELECTED_TEMPLATES,
      setSavePsd: MUTATIONS.SET_SAVE_PSD,
      setCreateIndividualAssets: MUTATIONS.SET_CREATE_INDIVIDUAL_ASSETS,
      setTreatMultipleSpacesAsSingle: MUTATIONS.SET_TREAT_MULTIPLE_SPACES_AS_SINGLE,
      setPreviewTemplateThumbnail: MUTATIONS.SET_PREVIEW_TEMPLATE_THUMBNAIL,
      setReplaceExistingProduct: MUTATIONS.SET_IS_REPLACE_EXISTING_PRODUCT,
      setRemovePlaceholderText: MUTATIONS.SET_REMOVE_PLACEHOLDER_TEXT,
      setUseSourceTextSettings: MUTATIONS.SET_USE_SOURCE_TEXT_SETTINGS,
      setIsUseProductImagesFromPSD: MUTATIONS.SET_IS_USE_PRODUCT_IMAGES_FROM_PSD,
      setIsGroupGroupedLayers: MUTATIONS.SET_IS_GROUP_GROUPED_LAYERS,
      setIsResizeAllowed: MUTATIONS.SET_IS_RESIZE_ALLOWED,
      setIsOpenInBannerBuilder: MUTATIONS.SET_IS_OPEN_IN_BANNER_BUILDER,
      setIsImportWithoutLayers: MUTATIONS.SET_IS_IMPORT_WITHOUT_LAYERS,
      setTextGeometryMode: MUTATIONS.SET_TEXT_GEOMETRY_MODE,
      toggleLayer: MUTATIONS.TOGGLE_LAYER,
    }),
    iccProfileOpen() {
      IccProfileEventBroker.fire(EVENTS.INIT)
    },
    imageLoadedHandler() {
      this.isImageLoading = false;
    },
    back() {
      this.setSelectedTemplate({});
    },
    handlePreviewImageArtboardClick(template) {
      this.toggleLayer({ index: this.currentPreviewIndex, layer: template });
    }
  }
}
</script>

<style scoped>
.modal-body--view-type-selector {
  max-width: 110px;
}

.custom-tooltip {
  max-width: 200px;
}

.template-features-buttons {
  position: absolute;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
